import { PresentationType } from '@carboncopyco/types';
import type { IIdentifyPostData, IVisitPostData } from '@carboncopyco/types';
import Axios from 'axios';
import { settings } from '../../../settings';
import type {
  IUserClickPayload,
  IUserVisitPayload,
} from '../../main/scripts/trackUserActivity';
import { CLICK_KEY, VISIT_KEY } from '../constants/constants';

const BASE_PATH = '/api/v1';
const BASE_PATH_V2 = '/api/v2';
const BASE_URL = settings.ANALYTICS_DEMOSTACK_API;

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-EVENT-API-KEY': settings.ANALYTICS_DEMOSTACK_API_KEY!,
  'Access-Control-Allow-Origin': '*',
};

export interface IdentifyResponse {
  session_id: string;
}
export interface VisitResponse {
  session_id: string;
}

const event: {
  identify: (
    data: Omit<IIdentifyPostData, 'tour_id'>,
    type: PresentationType
  ) => Promise<{ data: IdentifyResponse }>;
  visit: (data: IVisitPostData) => Promise<VisitResponse>;
  heartbeat: (sessionId: string) => Promise<any>;
} = {
  identify: (
    data: Omit<IIdentifyPostData, 'tour_id'>,
    type: PresentationType
  ) => {
    return Axios.request({
      url:
        type === PresentationType.share
          ? `${BASE_URL}${BASE_PATH}/identify-share`
          : `${BASE_URL}${BASE_PATH}/identify`,
      method: 'POST' as 'post',
      headers,
      data,
    });
  },
  visit: (data: IVisitPostData) => {
    return Axios.request({
      url: `${BASE_URL}${BASE_PATH}/visit`,
      method: 'POST' as 'post',
      headers,
      data: JSON.stringify(data),
    });
  },
  heartbeat: (sessionId: string) => {
    return Axios.request({
      url: `${BASE_URL}${BASE_PATH}/heartbeat`,
      method: 'POST' as 'post',
      headers,
      data: JSON.stringify({ session_id: sessionId }),
    });
  },
};

// TODO move all interfaces to @carboncopyco/types
export interface ISessionData {
  user: {
    name: string;
    email: string;
    role?: string;
  };
  session_id: string;
  organization_id: number | string;
  record_id: number | string;
  revision_id: number | string;
}

interface IUserVisitData extends IUserVisitPayload, ISessionData {}

interface IUserClickData extends IUserClickPayload, ISessionData {
  resource_type: string;
}
function generateVisitData(clickData: IUserClickData): IUserVisitData {
  const sessionData: ISessionData = {
    user: clickData.user,
    session_id: clickData.session_id,
    organization_id: clickData.organization_id,
    record_id: clickData.record_id,
    revision_id: clickData.revision_id,
  };
  const url = decodeURIComponent(clickData.location);
  const visitData = {
    title: '',
    url: encodeURIComponent(url),
    domain: new URL(url).hostname,
    path: new URL(url).pathname,
    xpath: clickData.xpath,
  };
  return { ...sessionData, ...visitData };
}

const eventV2: {
  startSession: (data: ISessionData) => void;
  endSession: (sessionId?: string) => void;
  visit: (data: IUserVisitData) => void;
  click: (data: IUserClickData) => void;
} = {
  startSession: async (data: ISessionData) => {
    try {
      await Axios.request({
        url: `${BASE_URL}${BASE_PATH_V2}/session/record/start`,
        method: 'POST' as 'post',
        headers,
        data: JSON.stringify(data),
      });
    } catch (e) {
      console.error('Analytics API(V2) - startSession error', e);
    }
  },
  endSession: async (sessionId?: string) => {
    if (!sessionId) {
      console.error('Analytics API(V2) - Session ID is not provided');
    }
    try {
      await Axios.request({
        url: `${BASE_URL}${BASE_PATH_V2}/session/record/end`,
        method: 'PUT' as 'put',
        headers,
        data: JSON.stringify({ session_id: sessionId }),
      });
    } catch (e) {
      console.error('Analytics API(V2) - endSession error', e);
    }
  },
  visit: async (data: IUserVisitData) => {
    try {
      await Axios.request({
        url: `${BASE_URL}${BASE_PATH_V2}/session/${VISIT_KEY}`,
        method: 'POST' as 'post',
        headers,
        data: JSON.stringify(data),
      });
    } catch (e) {
      console.error('Analytics API(V2) - visit error', e);
    }
  },
  click: async (data: IUserClickData) => {
    try {
      await Axios.request({
        url: `${BASE_URL}${BASE_PATH_V2}/session/${CLICK_KEY}`,
        method: 'POST' as 'post',
        headers,
        data: JSON.stringify(data),
      });
      eventV2.visit(generateVisitData(data));
    } catch (e) {
      console.error('Analytics API(V2) - click error', e);
    }
  },
};

const api = {
  event,
  eventV2,
};

export default api;
